import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { setUnityVolume, setUnityMuted } from "../lib/unityCommandBridge";
import { CHANNELS } from "../lib/audioChannelIcons";

export default function AudioSettingsPanel({ onClose, channels, onChannelChange }) {
  const panelRef = useRef(null);

  const handleVolumeChange = useCallback((key, raw) => {
    const value = parseFloat(raw);
    onChannelChange(key, { volume: value });
    setUnityVolume(key, value);
  }, [onChannelChange]);

  const handleMuteToggle = useCallback((key) => {
    const next = !channels[key].muted;
    onChannelChange(key, { muted: next });
    setUnityMuted(key, next);
  }, [channels, onChannelChange]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return createPortal(
    <div
      ref={panelRef}
      className="fixed top-[54px] right-4 z-[100000] w-64 rounded-2xl border border-white/10 bg-slate-900/95 backdrop-blur-xl shadow-2xl p-5 font-sans"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <span className="text-sm font-bold text-slate-100 tracking-wide">Audio Settings</span>
        <button
          onClick={onClose}
          className="bg-transparent border-none p-0.5 text-base leading-none text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
        >
          ✕
        </button>
      </div>

      {/* Channels */}
      <div className="flex flex-col gap-5">
        {CHANNELS.map(({ key, label, icon }) => {
          const { volume, muted } = channels[key];
          return (
            <div key={key}>
              {/* Label row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">{icon}</span>
                  <span className={`text-xs font-semibold transition-colors ${muted ? "text-slate-600" : "text-slate-300"}`}>
                    {label}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500 w-8 text-right tabular-nums">
                    {muted ? "—" : `${Math.round(volume * 100)}%`}
                  </span>
                  <button
                    onClick={() => handleMuteToggle(key)}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border cursor-pointer transition-all ${
                      muted
                        ? "bg-red-500/20 border-red-500/40 text-red-400 hover:bg-red-500/30"
                        : "bg-white/10 border-white/10 text-slate-400 hover:bg-white/20 hover:text-slate-200"
                    }`}
                  >
                    {muted ? "Muted" : "Mute"}
                  </button>
                </div>
              </div>

              {/* Slider */}
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                disabled={muted}
                onChange={(e) => handleVolumeChange(key, e.target.value)}
                className={`w-full h-1 rounded-full appearance-none outline-none accent-[#4DB8B5] transition-opacity ${
                  muted ? "opacity-30 cursor-not-allowed" : "opacity-100 cursor-pointer"
                }`}
              />
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <p className="mt-5 text-[10px] text-slate-600 leading-relaxed">
        Unity saves your volume settings between sessions.
      </p>
    </div>,
    document.body
  );
}
